import { formatMoney } from '../format';
import type { Statement } from '../types';

const STATEMENT_STATUS_ICON: Record<Statement['statementStatus'], string> = { Original: '○', Corrected: '✎', Voided: '✕' };

function StatementCard({ statement, defaultOpen }: { statement: Statement; defaultOpen: boolean }) {
  const s = statement;
  const label = `${s.employeeDisplayName} - statement ${s.sourcePayStatementId}`;
  return (
    <details className="phi-statement" open={defaultOpen}>
      <summary>
        {label} <span aria-hidden="true">{STATEMENT_STATUS_ICON[s.statementStatus]}</span> ({s.statementStatus}
        {s.statementStatus === 'Voided' ? ', excluded from totals' : ''})
      </summary>
      {s.correctsSourcePayStatementId && <p className="muted">Corrects statement {s.correctsSourcePayStatementId}.</p>}
      <dl className="phi-facts">
        <dt>Gross pay</dt><dd>{formatMoney(s.grossPay)}</dd>
        <dt>Taxable wages</dt><dd>{formatMoney(s.taxableWages)}</dd>
        <dt>Pre-tax deductions</dt><dd>{formatMoney(s.preTaxDeductions)}</dd>
        <dt>Post-tax deductions</dt><dd>{formatMoney(s.postTaxDeductions)}</dd>
        <dt>Employee taxes</dt><dd>{formatMoney(s.employeeTaxes)}</dd>
        <dt>Employer taxes</dt><dd>{formatMoney(s.employerTaxes)}</dd>
        <dt>Reimbursements</dt><dd>{formatMoney(s.reimbursements)}</dd>
        <dt>Net pay</dt><dd>{formatMoney(s.netPay)}</dd>
        <dt>Payment method</dt><dd>{s.paymentMethodDescription ?? '—'}</dd>
        <dt>Payment reference</dt><dd>{s.paymentReference ?? '—'}</dd>
      </dl>

      <div className="table-scroll">
        <table>
          <caption>Earnings for {label}</caption>
          <thead>
            <tr>
              <th scope="col">Code</th><th scope="col">Category</th><th scope="col" className="num">Hours</th>
              <th scope="col" className="num">Rate</th><th scope="col" className="num">Amount</th><th scope="col">Taxable</th>
            </tr>
          </thead>
          <tbody>
            {s.earnings.map((e) => (
              <tr key={e.sourceLineId}>
                <th scope="row">{e.code}</th>
                <td>{e.category ?? '—'}</td>
                <td className="num">{e.hours ?? '—'}</td>
                <td className="num">{e.rate === null ? '—' : formatMoney(e.rate)}</td>
                <td className="num">{formatMoney(e.amount)}</td>
                <td>{e.isTaxable ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-scroll">
        <table>
          <caption>Deductions for {label}</caption>
          <thead>
            <tr>
              <th scope="col">Code</th><th scope="col">Type</th><th scope="col" className="num">Employee amount</th>
              <th scope="col" className="num">Employer amount</th>
            </tr>
          </thead>
          <tbody>
            {s.deductions.map((d) => (
              <tr key={d.sourceLineId}>
                <th scope="row">{d.code}</th>
                <td>{d.classification === 'PreTax' ? 'Pre-tax' : 'Post-tax'}</td>
                <td className="num">{formatMoney(d.employeeAmount)}</td>
                <td className="num">{formatMoney(d.employerAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-scroll">
        <table>
          <caption>Taxes for {label}</caption>
          <thead>
            <tr>
              <th scope="col">Tax</th><th scope="col">Jurisdiction</th><th scope="col">Paid by</th>
              <th scope="col" className="num">Taxable wage base</th><th scope="col" className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {s.taxes.map((t) => (
              <tr key={t.sourceLineId}>
                <th scope="row">{t.name}</th>
                <td>{t.jurisdiction ?? '—'}</td>
                <td>{t.scope}</td>
                <td className="num">{t.taxableWageBase === null ? '—' : formatMoney(t.taxableWageBase)}</td>
                <td className="num">{formatMoney(t.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/** Read-only statement-level and line-level detail. Deliberately has no action controls of any kind. */
export function StatementList({ statements, defaultOpen = false }: { statements: Statement[]; defaultOpen?: boolean }) {
  if (statements.length === 0) return <p className="muted">No pay statements in this run.</p>;
  return (
    <div>
      {statements.map((s) => (
        <StatementCard key={s.sourcePayStatementId} statement={s} defaultOpen={defaultOpen} />
      ))}
    </div>
  );
}
